import { Link } from 'react-router-dom';
import SeoHead from './SeoHead';
import { Breadcrumbs } from './ComponentLibrary';
import { pageMeta } from '../seoUtils';
import { ADMIN_FEATURE_FLAGS } from '../config/adminFeatureFlags';

function TermsPage() {
  const seo = pageMeta({
    title: 'Terms of Service',
    description:
      "Review the terms and conditions for using NaijaNest, Nigeria's property marketplace.",
    path: '/terms',
  });
  return (
    <div className="static-page terms-page">
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Terms of Service', current: true },
        ]}
      />
      <section className="section">
        <header className="terms-page__intro">
          <h1>Terms of Service</h1>
          <p className="static-page__lead">
            Effective date: 2 August 2026. Last updated: 2 August 2026.
          </p>
        </header>

        <p>
          Welcome to <strong>NaijaNest</strong>. These Terms of Service (&quot;Terms&quot;) govern
          your access to and use of the NaijaNest website, mobile applications, and related
          services (collectively, the &quot;Platform&quot;).
        </p>
        <p>
          By accessing or using the Platform, you agree to be bound by these Terms. If you do not
          agree, you should not access or use the Platform.
        </p>

        <h2>1. About NaijaNest</h2>
        <p>
          NaijaNest is an online property marketplace operated by{' '}
          <strong>NaijaNest Prime Hub (RC 9619830)</strong>.
        </p>
        <p>
          The Platform enables property owners, developers, estate agencies, agents and other
          authorised advertisers to advertise property opportunities and enables users to
          discover and enquire about those opportunities.
        </p>
        <p>Unless expressly stated otherwise, NaijaNest:</p>
        <ul>
          <li>operates only as a digital marketplace;</li>
          <li>does not own listed properties;</li>
          <li>does not act as an estate agent, broker, legal adviser or financial adviser;</li>
          <li>does not negotiate transactions on behalf of users; and</li>
          <li>
            is not a party to agreements entered into between buyers, sellers, landlords, tenants
            or advertisers.
          </li>
        </ul>

        <h2>2. Eligibility</h2>
        <p>To use the Platform you must:</p>
        <ul>
          <li>be at least 18 years old;</li>
          <li>provide accurate and truthful information;</li>
          <li>have the legal capacity to enter into agreements;</li>
          <li>comply with all applicable laws and regulations.</li>
        </ul>

        <h2>3. User Accounts</h2>
        <p>You are responsible for:</p>
        <ul>
          <li>maintaining the confidentiality of your login credentials;</li>
          <li>all activities carried out using your account;</li>
          <li>ensuring your profile information remains accurate and up to date.</li>
        </ul>
        <p>You must notify NaijaNest immediately if you believe your account has been compromised.</p>
        <p>
          NaijaNest may suspend or terminate accounts that violate these Terms or pose a risk to
          the Platform or its users.
        </p>

        <h2>4. Marketplace Services</h2>
        <p>The Platform may provide services including:</p>
        <ul>
          <li>browsing property listings;</li>
          <li>searching and filtering properties;</li>
          <li>saving favourite properties;</li>
          <li>contacting advertisers;</li>
          <li>creating and managing property listings;</li>
          <li>receiving enquiries;</li>
          <li>reporting suspicious listings.</li>
        </ul>
        <p>NaijaNest may add, modify or discontinue features at any time without prior notice.</p>

        <h2>5. Property Listings</h2>
        <p>Advertisers are solely responsible for the content they publish.</p>
        <p>By publishing a listing, you confirm that:</p>
        <ul>
          <li>the information is accurate to the best of your knowledge;</li>
          <li>you have authority to advertise the property;</li>
          <li>photographs and other content may legally be used;</li>
          <li>pricing and descriptions are not intentionally misleading.</li>
        </ul>
        <p>
          NaijaNest reserves the right to edit, reject, suspend or remove listings that violate
          these Terms, applicable laws or Platform policies.
        </p>

        <h2>6. User Responsibilities</h2>
        <p>You agree to:</p>
        <ul>
          <li>provide truthful information;</li>
          <li>communicate respectfully with other users;</li>
          <li>comply with applicable laws;</li>
          <li>use the Platform only for lawful purposes;</li>
          <li>respect the intellectual property rights of others.</li>
        </ul>

        <h2>7. Due Diligence</h2>
        <p>
          Users are solely responsible for conducting their own due diligence before entering
          into any property transaction.
        </p>
        <p>We strongly recommend that you:</p>
        <ul>
          <li>inspect the property in person where possible;</li>
          <li>verify the advertiser&rsquo;s identity;</li>
          <li>confirm ownership or authority to advertise;</li>
          <li>verify title documents through appropriate professionals or authorities;</li>
          <li>seek independent legal or professional advice where appropriate;</li>
          <li>avoid making payments until you are satisfied with your own verification.</li>
        </ul>

        <h2 id="verification">8. Verification</h2>
        <p>
          NaijaNest may provide verification badges or statuses for users, businesses or
          listings.
        </p>
        <p>
          Unless expressly stated otherwise, verification only confirms that specified documents
          or information have been reviewed as part of NaijaNest&rsquo;s verification process.
        </p>
        <p>Verification does <strong>not</strong> guarantee:</p>
        <ul>
          <li>ownership of a property;</li>
          <li>authenticity of title documents;</li>
          <li>legal rights to sell or lease;</li>
          <li>property condition;</li>
          <li>investment performance;</li>
          <li>transaction safety;</li>
          <li>the accuracy of every statement made within a listing.</li>
        </ul>
        <p>Users should continue to carry out their own independent due diligence.</p>

        <h2>9. Fees and Payments</h2>
        <p>
          Where listing fees, subscriptions or other paid services are offered, the applicable
          pricing will be displayed before purchase.
        </p>
        <p>
          Unless expressly stated otherwise, NaijaNest does not receive, hold or process payments
          made between buyers, sellers, landlords, tenants or advertisers.
        </p>
        <p>
          Users remain solely responsible for any agreements and payments they make with one
          another.
        </p>

        <h2>10. Prohibited Activities</h2>
        <ul>
          <li>publish false or misleading listings;</li>
          <li>advertise properties without appropriate authority;</li>
          <li>impersonate another individual or business;</li>
          <li>upload unlawful, defamatory or infringing content;</li>
          <li>upload stolen or unauthorised photographs;</li>
          <li>create fake accounts;</li>
          <li>attempt to manipulate search rankings or platform functionality;</li>
          <li>scrape, copy or harvest Platform data without permission;</li>
          <li>interfere with Platform security or availability;</li>
          <li>distribute malware or malicious software;</li>
          <li>use the Platform for illegal or fraudulent purposes.</li>
        </ul>

        <h2>11. Intellectual Property</h2>
        <p>
          NaijaNest and its licensors own all rights in the Platform, including its software,
          branding, design, trademarks and logos.
        </p>
        <p>
          Users retain ownership of content they upload but grant NaijaNest a non-exclusive,
          worldwide, royalty-free licence to host, display, reproduce and distribute that content
          solely for the purpose of operating, promoting and improving the Platform.
        </p>

        <h2>12. Third-Party Services</h2>
        <p>
          The Platform may contain links to third-party websites, messaging services or other
          services.
        </p>
        <p>
          NaijaNest is not responsible for the availability, content, policies or practices of
          third-party services.
        </p>
        <p>Your use of those services is governed by their own terms and policies.</p>

        <h2 id="disclaimer">13. Disclaimer</h2>
        <p>NaijaNest provides an online marketplace for property advertisements.</p>
        <p>Unless expressly stated otherwise, NaijaNest:</p>
        <ul>
          <li>does not own listed properties;</li>
          <li>does not inspect every property;</li>
          <li>does not verify every advertiser or listing;</li>
          <li>
            does not guarantee the accuracy, completeness, legality, ownership, availability,
            pricing or condition of any property;
          </li>
          <li>does not guarantee the outcome of transactions between users.</li>
        </ul>
        <p>
          Property advertisements are supplied by advertisers, who remain solely responsible for
          their content.
        </p>
        <p>
          Users should independently verify all information before making decisions or entering
          into agreements.
        </p>

        <h2>14. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by applicable law, NaijaNest shall not be liable for
          any indirect, incidental, consequential, special or punitive loss or damage arising
          from:
        </p>
        <ul>
          <li>the use of, or inability to use, the Platform;</li>
          <li>reliance on information contained within listings;</li>
          <li>communications between users;</li>
          <li>transactions entered into between users;</li>
          <li>the acts or omissions of advertisers or other third parties.</li>
        </ul>
        <p>
          Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or
          limited under applicable law.
        </p>

        <h2>15. Indemnity</h2>
        <p>
          You agree to indemnify and hold harmless NaijaNest, its directors, officers, employees
          and affiliates against claims, liabilities, losses, costs and expenses arising from:
        </p>
        <ul>
          <li>your breach of these Terms;</li>
          <li>your listings or uploaded content;</li>
          <li>your misuse of the Platform;</li>
          <li>your violation of applicable laws or the rights of another person.</li>
        </ul>

        <h2>16. Suspension and Termination</h2>
        <p>
          NaijaNest may suspend, restrict or terminate your account or remove listings where it
          reasonably believes that you have:
        </p>
        <ul>
          <li>breached these Terms;</li>
          <li>engaged in fraud or suspected fraud;</li>
          <li>provided misleading information;</li>
          <li>compromised the security or integrity of the Platform;</li>
          <li>engaged in unlawful activity.</li>
        </ul>
        <p>
          Where appropriate, NaijaNest may cooperate with law enforcement or regulatory
          authorities.
        </p>

        <h2>17. Changes to these Terms</h2>
        <p>NaijaNest may update these Terms from time to time.</p>
        <p>
          The latest version will always be published on the Platform together with its Effective
          Date and Last Updated date.
        </p>
        <p>
          Continued use of the Platform after changes become effective constitutes acceptance of
          the updated Terms.
        </p>

        <h2>18. Governing Law</h2>
        <p>
          These Terms shall be governed by and interpreted in accordance with the laws of the
          Federal Republic of Nigeria, unless mandatory laws in another applicable jurisdiction
          require otherwise.
        </p>

        <h2>19. Contact Us</h2>
        <p>
          If you have questions about these Terms or wish to contact us regarding legal or
          compliance matters, please <Link to="/contact">contact NaijaNest</Link> using the
          contact details published on the Platform.
        </p>

        <h2>Related Policies</h2>
        <p>Please also review our:</p>
        <ul>
          <li>
            <Link to="/privacy">Privacy Policy</Link>
          </li>
          <li>
            <Link to="/cookie-policy">Cookie Policy</Link>
          </li>
          <li>
            <Link to="/listing-policy">Listing Policy</Link>
          </li>
          {ADMIN_FEATURE_FLAGS.agentRequests && (
            <li>
              <Link to="/agent-verification-policy">Agent Verification Policy</Link>
            </li>
          )}
          <li>
            <Link to="/safety">Safety Centre</Link>
          </li>
        </ul>
        <p>These policies form an important part of your use of the Platform.</p>
      </section>
    </div>
  );
}
export default TermsPage;
